import { inject } from '@angular/core';
import { Router, CanActivateFn } from '@angular/router';
import { AuthService } from '@crowdctrl/shared';
import { map, take, switchMap } from 'rxjs/operators';
import { of, from } from 'rxjs';

/**
 * Auth guard to protect routes that require USER or TENANT_ADMIN authentication.
 * Redirects to login if user is not authenticated.
 * Redirects to unauthorized if user doesn't have USER or TENANT_ADMIN role.
 */
export const authGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  return authService.authState$.pipe(
    take(1),
    switchMap((user) => {
      if (!user) {
        router.navigate(['/login']);
        return of(false);
      }

      // Check if user has access to Agent app via custom claim
      return from(authService.getCustomClaim('canAccessAgent')).pipe(
        switchMap((canAccessAgent) => {
          if (!canAccessAgent) {
            router.navigate(['/unauthorized']);
            return of(false);
          }

          // Check if user has USER or TENANT_ADMIN role by decoding token
          // Note: Workspace-only users may not have a role claim, which is OK
          return from(authService.getUserRole()).pipe(
            switchMap((role) => {
              // If user has a role, it must be USER or TENANT_ADMIN
              if (role && role !== 'USER' && role !== 'TENANT_ADMIN') {
                router.navigate(['/unauthorized']);
                return of(false);
              }

              // If no role, check if they have workspace invites (workspace-only user)
              if (!role) {
                return from(authService.getCustomClaim('workspaceInvites')).pipe(
                  map((invites) => {
                    if (!invites || (Array.isArray(invites) && invites.length === 0)) {
                      router.navigate(['/unauthorized']);
                      return false;
                    }
                    return true;
                  }),
                );
              }

              // User has valid role (USER or TENANT_ADMIN)
              return of(true);
            }),
          );
        }),
      );
    }),
  );
};
